// ============================================================================
// BOQ — Measurements (taking-off) hook
// ============================================================================
// Project-scoped measurements with their line links. Geometry (Nr/L/B/H) is
// entered for `prism` rows and the four measures are computed on write;
// `direct` rows hold typed running_m / plan_area / elevation_area / volume
// (polygonal or amorphous shapes).
//
// Deductions live on the LINK, not the measurement: the same window is an
// addition on the Windows line and a deduction on the brick line. So each
// link carries is_deduction, and sumMeasureForLine applies the per-link sign.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError } from '../../hooks/useSupabase';

const SELECT = `
  id, project_id, description, mode, nr, length, breadth, height,
  running_m, plan_area, elevation_area, volume,
  project_zone_id, level, grid_ref, location_note, sequence_order,
  project_item_measurements ( project_item_id, is_deduction )
`.replace(/\s+/g, ' ');

const num = (v) => (v === null || v === undefined ? null : Number(v));

const mapLinks = (joins) => (joins || []).map((l) => ({ itemId: l.project_item_id, isDeduction: !!l.is_deduction }));

const mapRow = (m) => {
  const links = mapLinks(m.project_item_measurements);
  return {
    id: m.id,
    projectId: m.project_id,
    description: m.description,
    mode: m.mode || 'prism',
    nr: num(m.nr),
    length: num(m.length),
    breadth: num(m.breadth),
    height: num(m.height),
    runningM: num(m.running_m),
    planArea: num(m.plan_area),
    elevationArea: num(m.elevation_area),
    volume: num(m.volume),
    projectZoneId: m.project_zone_id,
    level: m.level,
    gridRef: m.grid_ref,
    locationNote: m.location_note,
    sequenceOrder: m.sequence_order,
    links,
    itemIds: links.map((l) => l.itemId),
  };
};

// Magnitude a measurement contributes under a basis (sign applied separately).
export function measureValue(m, basis) {
  switch (basis) {
    case 'count':          return m.nr ?? 0;
    case 'running_m':      return m.runningM ?? 0;
    case 'plan_area':      return m.planArea ?? 0;
    case 'elevation_area': return m.elevationArea ?? 0;
    case 'volume':         return m.volume ?? 0;
    default:               return 0;
  }
}

// Unsigned sum (used where a single line's deduction sign doesn't apply).
export function sumMeasure(measurements, basis) {
  if (!basis) return null;
  return measurements.reduce((acc, m) => acc + measureValue(m, basis), 0);
}

// Signed sum for one line: deduction links subtract. Only measurements linked
// to itemId contribute.
export function sumMeasureForLine(measurements, basis, itemId) {
  if (!basis) return null;
  return measurements.reduce((acc, m) => {
    const link = (m.links || []).find((l) => l.itemId === itemId);
    if (!link) return acc;
    const sign = link.isDeduction ? -1 : 1;
    return acc + sign * measureValue(m, basis);
  }, 0);
}

const numOrNull = (v) => (v === '' || v === undefined || v === null ? null : Number(v));

function computeMeasures(mode, nr, length, breadth, height, direct) {
  if (mode === 'direct') {
    return {
      running_m: numOrNull(direct.runningM),
      plan_area: numOrNull(direct.planArea),
      elevation_area: numOrNull(direct.elevationArea),
      volume: numOrNull(direct.volume),
    };
  }
  const N = nr == null ? 1 : nr;
  return {
    running_m: length == null ? null : N * length,
    plan_area: (length == null || breadth == null) ? null : N * length * breadth,
    elevation_area: (length == null || height == null) ? null : N * length * height,
    volume: (length == null || breadth == null || height == null) ? null : N * length * breadth * height,
  };
}

const writePayload = (input) => {
  const mode = input.mode === 'direct' ? 'direct' : 'prism';
  const nr = input.nr === '' || input.nr === undefined ? 1 : Number(input.nr);
  const length = numOrNull(input.length);
  const breadth = numOrNull(input.breadth);
  const height = numOrNull(input.height);
  const measures = computeMeasures(mode, nr, length, breadth, height, {
    runningM: input.runningM, planArea: input.planArea, elevationArea: input.elevationArea, volume: input.volume,
  });
  return {
    description: input.description?.trim() || null,
    mode,
    nr,
    length,
    breadth,
    height,
    ...measures,
    project_zone_id: input.projectZoneId || null,
    level: input.level?.trim() || null,
    grid_ref: input.gridRef?.trim() || null,
    location_note: input.locationNote?.trim() || null,
    sequence_order: input.sequenceOrder ?? 0,
  };
};

// Normalise a links spec: accept input.links [{itemId,isDeduction}] or a plain
// itemIds array (treated as additions).
const linksFromInput = (input) => {
  if (Array.isArray(input.links)) return input.links.map((l) => ({ itemId: l.itemId, isDeduction: !!l.isDeduction }));
  return (input.itemIds || []).map((itemId) => ({ itemId, isDeduction: false }));
};

export const useBoqMeasurements = (boqProjectId) => {
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!boqProjectId) { setMeasurements([]); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase
        .schema('boq')
        .from('measurements')
        .select(SELECT)
        .eq('project_id', boqProjectId)
        .is('deleted_at', null)
        .order('sequence_order')
        .order('created_at');
      if (fetchErr) throw fetchErr;
      setMeasurements((data || []).map(mapRow));
    } catch (err) {
      console.error('[useBoqMeasurements] fetch failed', err);
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'measurements' }));
    } finally {
      setLoading(false);
    }
  }, [boqProjectId]);

  const createMeasurement = useCallback(async (itemId, input, opts = {}) => {
    if (!boqProjectId) return { success: false, error: 'No BOQ project context.' };
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to add a measurement.' };
      }
      const { data: mRow, error: insErr } = await supabase
        .schema('boq').from('measurements')
        .insert({ project_id: boqProjectId, ...writePayload(input) })
        .select(SELECT).single();
      if (insErr) throw insErr;

      const isDeduction = !!opts.isDeduction;
      if (itemId) {
        const { error: linkErr } = await supabase
          .schema('boq').from('project_item_measurements')
          .insert({ project_item_id: itemId, measurement_id: mRow.id, is_deduction: isDeduction });
        if (linkErr) throw linkErr;
      }
      const mapped = mapRow({ ...mRow, project_item_measurements: itemId ? [{ project_item_id: itemId, is_deduction: isDeduction }] : [] });
      setMeasurements((prev) => [...prev, mapped]);
      return { success: true, measurement: mapped };
    } catch (err) {
      console.error('[useBoqMeasurements] create failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'measurement' }) };
    }
  }, [boqProjectId]);

  const updateMeasurement = useCallback(async (id, input) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to update a measurement.' };
      }
      const { data, error: updErr } = await supabase
        .schema('boq').from('measurements')
        .update(writePayload(input)).eq('id', id).select(SELECT).single();
      if (updErr) throw updErr;
      const mapped = mapRow(data);
      setMeasurements((prev) => prev.map((m) => (m.id === id ? mapped : m)));
      return { success: true, measurement: mapped };
    } catch (err) {
      console.error('[useBoqMeasurements] update failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'measurement' }) };
    }
  }, []);

  const deleteMeasurement = useCallback(async (id) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to delete a measurement.' };
      }
      const { error: delErr } = await supabase.schema('boq').from('measurements').delete().eq('id', id);
      if (delErr) throw delErr;
      setMeasurements((prev) => prev.filter((m) => m.id !== id));
      return { success: true };
    } catch (err) {
      console.error('[useBoqMeasurements] delete failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'measurement' }) };
    }
  }, []);

  const createMeasurementsMany = useCallback(async (itemId, inputs, opts = {}) => {
    if (!boqProjectId) return { success: false, error: 'No BOQ project context.' };
    if (!Array.isArray(inputs) || inputs.length === 0) return { success: true, measurements: [] };
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to import measurements.' };
      }
      const { data: rows, error: insErr } = await supabase
        .schema('boq').from('measurements')
        .insert(inputs.map((i) => ({ project_id: boqProjectId, ...writePayload(i) }))).select(SELECT);
      if (insErr) throw insErr;
      const isDeduction = !!opts.isDeduction;
      if (itemId && rows?.length) {
        const links = rows.map((r) => ({ project_item_id: itemId, measurement_id: r.id, is_deduction: isDeduction }));
        const { error: linkErr } = await supabase.schema('boq').from('project_item_measurements').insert(links);
        if (linkErr) throw linkErr;
      }
      const mapped = (rows || []).map((r) =>
        mapRow({ ...r, project_item_measurements: itemId ? [{ project_item_id: itemId, is_deduction: isDeduction }] : [] }));
      setMeasurements((prev) => [...prev, ...mapped]);
      return { success: true, measurements: mapped };
    } catch (err) {
      console.error('[useBoqMeasurements] createMany failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'import', entity: 'measurements' }) };
    }
  }, [boqProjectId]);

  // Link an existing measurement to a line (optionally as a deduction).
  const linkMeasurement = useCallback(async (itemId, measurementId, isDeduction = false) => {
    try {
      const { error: linkErr } = await supabase
        .schema('boq').from('project_item_measurements')
        .insert({ project_item_id: itemId, measurement_id: measurementId, is_deduction: !!isDeduction });
      if (linkErr) throw linkErr;
      setMeasurements((prev) => prev.map((m) => {
        if (m.id !== measurementId || m.links.some((l) => l.itemId === itemId)) return m;
        const links = [...m.links, { itemId, isDeduction: !!isDeduction }];
        return { ...m, links, itemIds: links.map((l) => l.itemId) };
      }));
      return { success: true };
    } catch (err) {
      console.error('[useBoqMeasurements] link failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'link', entity: 'measurement' }) };
    }
  }, []);

  // Flip an existing link's deduction flag (the "Deduct" toggle in a sheet).
  const setLinkDeduction = useCallback(async (itemId, measurementId, isDeduction) => {
    try {
      const { error: updErr } = await supabase
        .schema('boq').from('project_item_measurements')
        .update({ is_deduction: !!isDeduction })
        .eq('project_item_id', itemId).eq('measurement_id', measurementId);
      if (updErr) throw updErr;
      setMeasurements((prev) => prev.map((m) => {
        if (m.id !== measurementId) return m;
        const links = m.links.map((l) => (l.itemId === itemId ? { ...l, isDeduction: !!isDeduction } : l));
        return { ...m, links };
      }));
      return { success: true };
    } catch (err) {
      console.error('[useBoqMeasurements] setLinkDeduction failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'measurement link' }) };
    }
  }, []);

  const unlinkMeasurement = useCallback(async (itemId, measurementId) => {
    try {
      const { error: delErr } = await supabase
        .schema('boq').from('project_item_measurements')
        .delete().eq('project_item_id', itemId).eq('measurement_id', measurementId);
      if (delErr) throw delErr;
      setMeasurements((prev) => prev.map((m) => {
        if (m.id !== measurementId) return m;
        const links = m.links.filter((l) => l.itemId !== itemId);
        return { ...m, links, itemIds: links.map((l) => l.itemId) };
      }));
      return { success: true };
    } catch (err) {
      console.error('[useBoqMeasurements] unlink failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'unlink', entity: 'measurement' }) };
    }
  }, []);

  // Bulk-create measurements that each keep their own links (incl. deduction).
  // inputs: [{ ...geometry/location/mode, links: [{itemId,isDeduction}] | itemIds: [] }].
  const createMeasurementsWithLinks = useCallback(async (inputs) => {
    if (!boqProjectId) return { success: false, error: 'No BOQ project context.' };
    if (!Array.isArray(inputs) || inputs.length === 0) return { success: true, measurements: [] };
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to add measurements.' };
      }
      const { data: rows, error: insErr } = await supabase
        .schema('boq').from('measurements')
        .insert(inputs.map((i) => ({ project_id: boqProjectId, ...writePayload(i) }))).select(SELECT);
      if (insErr) throw insErr;

      const perRowLinks = inputs.map(linksFromInput);
      const joinRows = [];
      (rows || []).forEach((r, i) => {
        perRowLinks[i].forEach((l) => joinRows.push({ project_item_id: l.itemId, measurement_id: r.id, is_deduction: l.isDeduction }));
      });
      if (joinRows.length) {
        const { error: linkErr } = await supabase.schema('boq').from('project_item_measurements').insert(joinRows);
        if (linkErr) throw linkErr;
      }
      const mapped = (rows || []).map((r, i) =>
        mapRow({ ...r, project_item_measurements: perRowLinks[i].map((l) => ({ project_item_id: l.itemId, is_deduction: l.isDeduction })) }));
      setMeasurements((prev) => [...prev, ...mapped]);
      return { success: true, measurements: mapped };
    } catch (err) {
      console.error('[useBoqMeasurements] createWithLinks failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'measurements' }) };
    }
  }, [boqProjectId]);

  const deleteMeasurements = useCallback(async (ids) => {
    if (!ids || ids.length === 0) return { success: true };
    try {
      const { error: delErr } = await supabase.schema('boq').from('measurements').delete().in('id', ids);
      if (delErr) throw delErr;
      const idSet = new Set(ids);
      setMeasurements((prev) => prev.filter((m) => !idSet.has(m.id)));
      return { success: true };
    } catch (err) {
      console.error('[useBoqMeasurements] deleteMany failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'measurements' }) };
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return {
    measurements,
    loading,
    error,
    refresh: fetchAll,
    createMeasurement,
    createMeasurementsMany,
    createMeasurementsWithLinks,
    updateMeasurement,
    deleteMeasurement,
    deleteMeasurements,
    linkMeasurement,
    setLinkDeduction,
    unlinkMeasurement,
  };
};
